import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BLOG_POSTS, BLOG_META } from '../data/blogData';
import { BLOG_POSTS_EN, BLOG_META_EN } from '../data/blogDataEn';
import { BLOG_POSTS_FR, BLOG_META_FR } from '../data/blogDataFr';

const SUPPORTED = ['it', 'en', 'fr'];

/** Italian is the site's home language and is always the default. We do NOT
 *  auto-detect the browser locale — foreign visitors switch via the header
 *  selector, which writes `?lang=en` or `?lang=fr` to the URL. */
function detectBrowserLang() {
  return 'it';
}

/**
 * Reads the current language from the ?lang= query parameter.
 * If absent, auto-detects from the visitor's browser language.
 * The setter is preserved for programmatic overrides (kept for API-compat
 * with older callers even though the visible switcher has been removed).
 */
export function useBlogLang() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const raw = params.get('lang');
  const lang = SUPPORTED.includes(raw) ? raw : detectBrowserLang();

  const setLang = (next) => {
    const p = new URLSearchParams(location.search);
    if (next && SUPPORTED.includes(next)) p.set('lang', next);
    else p.delete('lang');
    const q = p.toString();
    navigate(location.pathname + (q ? '?' + q : ''), { replace: false });
  };

  return { lang, setLang };
}

/** Returns the BLOG_META object for the given language. */
export function useBlogMeta(lang) {
  return useMemo(() => {
    if (lang === 'en') return BLOG_META_EN;
    if (lang === 'fr') return BLOG_META_FR;
    return BLOG_META.it;
  }, [lang]);
}

/** Merges a base Italian post with its optional per-language overrides. */
export function localizePost(post, lang) {
  if (!post) return null;
  if (lang === 'en') {
    const overrides = BLOG_POSTS_EN[post.slug];
    return overrides ? { ...post, ...overrides } : post;
  }
  if (lang === 'fr') {
    const overrides = BLOG_POSTS_FR[post.slug];
    return overrides ? { ...post, ...overrides } : post;
  }
  return post;
}

/** Localize the whole list of posts. */
export function useLocalizedPosts(lang) {
  return useMemo(() => BLOG_POSTS.map((p) => localizePost(p, lang)), [lang]);
}
